import { useState, useEffect, useRef, useCallback } from 'react';
import useCoinGecko from './useCoinGecko';
import { MarketSnapshot } from '../types';
import { getCachedData, setCachedData } from '../utils/marketCache';
import { CORE_GECKO_IDS, SUPPORTED_SYMBOLS, SYMBOL_CONFIG } from '../utils/marketSymbols';

export interface MarketDataState {
  snapshots: Record<string, MarketSnapshot>;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
  refetch: () => void;
}

async function fetchBinanceData(pair: string): Promise<{ funding: number; oi: number; liqScore: number }> {
  const [fundingRes, oiRes, tickerRes] = await Promise.all([
    fetch(`https://fapi.binance.com/fapi/v1/premiumIndex?symbol=${pair}`),
    fetch(`https://fapi.binance.com/fapi/v1/openInterest?symbol=${pair}`),
    fetch(`https://fapi.binance.com/fapi/v1/ticker/24hr?symbol=${pair}`),
  ]);
  const [fundingData, oiData, tickerData] = await Promise.all([
    fundingRes.json(), oiRes.json(), tickerRes.json(),
  ]);
  const funding = parseFloat(fundingData.lastFundingRate || '0') * 100;
  const oi = parseFloat(oiData.openInterest || '0');
  const priceChange24h = parseFloat(tickerData.priceChangePercent || '0');
  const volumeRatio = parseFloat(tickerData.volume || '0') / (parseFloat(tickerData.quoteVolume || '1') || 1);
  const liqScore = Math.abs(priceChange24h) * volumeRatio;
  return { funding, oi, liqScore };
}

async function buildSnapshot(
  symbol: string,
  prevOi: number | null,
  geckoPrice: { price: number; priceChange24h: number }
): Promise<MarketSnapshot> {
  const cacheKey = `market_snapshot_${symbol}`;
  const cached = getCachedData<MarketSnapshot>(cacheKey);
  if (cached) return cached;

  const { binancePair } = SYMBOL_CONFIG[symbol];
  const binance = await fetchBinanceData(binancePair);

  const oiTrend: 'up' | 'down' | 'neutral' =
    prevOi === null ? 'neutral' : binance.oi > prevOi ? 'up' : binance.oi < prevOi ? 'down' : 'neutral';
  const liqTrend: 'up' | 'down' | 'neutral' =
    binance.liqScore > 0.5 ? 'up' : binance.liqScore < 0.1 ? 'down' : 'neutral';
  const longRatio = binance.funding >= 0
    ? 50 + Math.min(binance.funding * 500, 30)
    : 50 + Math.max(binance.funding * 500, -30);

  const snapshot: MarketSnapshot = {
    symbol,
    price: geckoPrice.price,
    priceChange24h: geckoPrice.priceChange24h,
    funding: binance.funding,
    oi: binance.oi,
    oiTrend,
    liqTrend,
    longRatio: Math.round(longRatio * 10) / 10,
    shortRatio: Math.round((100 - longRatio) * 10) / 10,
    fetchedAt: Date.now(),
  };

  setCachedData(cacheKey, snapshot);
  return snapshot;
}

export function useMarketData(): MarketDataState {
  const { ensureMarkets, refreshMarkets } = useCoinGecko();
  const [snapshots, setSnapshots] = useState<Record<string, MarketSnapshot>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  const prevOiRef = useRef<Record<string, number | null>>(
    Object.fromEntries(SUPPORTED_SYMBOLS.map((symbol) => [symbol, null]))
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAll = useCallback(async (forcePrices = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const geckoMarkets = forcePrices
        ? await refreshMarkets(CORE_GECKO_IDS)
        : await ensureMarkets(CORE_GECKO_IDS);

      const results = await Promise.allSettled(
        SUPPORTED_SYMBOLS.map((symbol) =>
          buildSnapshot(symbol, prevOiRef.current[symbol], {
            price: geckoMarkets[SYMBOL_CONFIG[symbol].geckoId]?.currentPrice ?? 0,
            priceChange24h: geckoMarkets[SYMBOL_CONFIG[symbol].geckoId]?.priceChangePercentage24h ?? 0,
          })
        )
      );

      const next: Record<string, MarketSnapshot> = {};
      results.forEach((result, index) => {
        const symbol = SUPPORTED_SYMBOLS[index];
        if (result.status === 'fulfilled') {
          next[symbol] = result.value;
          prevOiRef.current[symbol] = result.value.oi;
        }
      });

      setSnapshots(next);
      setLastUpdated(Date.now());
    } catch (fetchError) {
      setError('Failed to load market data.');
      console.error('useMarketData fetch error:', fetchError);
    } finally {
      setIsLoading(false);
    }
  }, [ensureMarkets, refreshMarkets]);

  useEffect(() => {
    void fetchAll();
    intervalRef.current = setInterval(() => { void fetchAll(); }, 3 * 60 * 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchAll]);

  const refetch = useCallback(() => {
    void fetchAll(true);
  }, [fetchAll]);

  return { snapshots, isLoading, error, lastUpdated, refetch };
}

export default useMarketData;
export { SUPPORTED_SYMBOLS };
